import {Component, ElementRef, OnInit, OnDestroy, ViewChild, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {Budget} from '../../../../DataModels/budget-model';
import {BudgetHeader} from '../../../../DataModels/budget-header-model';
import {DataService} from '../../../../Services/data.service';
import {ResponseModel} from '../../../../DataModels/response-model';
import {DataExportService} from '../../../../Services/dataExport.service';
import {CanComponentDeactivate} from '../../../../Interfaces/unsaved-changes.interface';
import {ConfirmDialogService} from '../../../../Services/confirm-dialog.service';
import {HttpParams} from '@angular/common/http';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {HelperService} from '../../../../Services/helper/helper.service';


@Component({
  selector: 'app-budget-form',
  templateUrl: './budget-form.component.html',
  styleUrls: ['./budget-form.component.scss'],
  standalone: false
})
export class BudgetFormComponent implements OnInit, OnDestroy, CanComponentDeactivate {

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: BsToastService,
    private dataService: DataService,
    private dataExportService: DataExportService,
    private confirmDialogService: ConfirmDialogService,
    private helperService: HelperService,
    private destroyRef: DestroyRef,
  ) {
  }
  // Direct class properties
  budget: Budget = {
    header: {} as BudgetHeader,
    netIncome: {}
  } as Budget;

  alert: any = {
    status: '',
    message: []
  };

  report: any[] = [];

  companies: any[] = [];
  locations: any[] = [];
  startDates: any[] = [];
  currencies: any[] = [];
  accounts: any[] = [];

  companyError = false;
  locationError = false;
  dateMissing = false;
  budgetDataMissing = false;
  descriptionError = false;
  currencyError = false;
  statusError = false;

  budgetTypeName = 'Regular';
  budgetTypeClass = 'budget-regular';
  total = 0;
  showBudget = false;

  rfType: any[] = [];
  rf1: any[] = [];
  rf2: any[] = [];
  rf3: any[] = [];
  rf4: any[] = [];
  rf5: any[] = [];

  rfValues: any[] = [];

  isLoading = true;
  isSubmitting = false;

  // list buttons config similar to bank
  buttonConfig: any = {
    save: true,
    saveClose: true,
    saveNew: true,
    delete: true,
    reset: true,
    close: true,
    print: false,
    email: false
  };

  // View model for components
  vm: any = {
    account: [],
    accountFilter: {search_string: '', type_name: ''},
    startAccount: 0,
    tempAccountDetailId: ''
  };

  // Drag-and-drop handled by Angular CDK

  developerView = false;
  showDelete = false;
  paid = false;
  originalDocument: any = {};
  budgetId = '';
  saveClose = false;
  budgetStartDate: any;
  budgetEndDate: any;

  @ViewChild('summaryTable') summaryTable: ElementRef;
  reportDataToSendInEmail: EmailModel;

  // used by changes guard
  checkForChanges(): boolean {
    return JSON.stringify(this.budget) !== JSON.stringify(this.originalDocument);
  }

  ngOnInit(): void {
    // Initialize header_date (budget initiated date) once if not set
    this.isLoading = true;
    if (!this.budget.header.header_date) {
      this.budget.header.header_date = new Date() as any;
    }

    // Initialize originalDocument early to ensure guard comparisons are stable
    // This will be updated once actual data is loaded
    this.originalDocument = structuredClone(this.budget);

    this.loadData();

    // Check if we're editing an existing budget (support query param id as used by list)
    const qid = this.route.snapshot.queryParamMap.get('id');
    if (qid) {
      this.budgetId = qid;
      this.loadBudget(qid);
    } else {
      this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
        if (params.id) {
          this.budgetId = params.id;
          this.loadBudget(params.id);
        } else {
          this.budgetId = '';
          this.initNewBudget();
        }
      });
    }

  }

  loadData(): void {
    this.isLoading = true;

    // Load companies
    this.getTableData('company', '?state=active', (data) => {
      this.companies = data;
      // console.log('cos', data);
      this.checkLoadingComplete();
    }).then();

    // Load locations
    this.getTableData('location', '?state=active', (data) => {
      this.locations = data;
      // console.log('locs', this.locations);
      this.checkLoadingComplete();
    }).then();

    // Load currencies
    this.getTableData('currency', '?state=active', (data) => {
      this.currencies = data;
      // console.log('cur', this.currencies);
      this.checkLoadingComplete();
    }).then();

    // Load accounts with history values
    this.getTableData('account', '?state=active', (data) => {
      // Process accounts to ensure they have all required properties
      this.accounts = this.processAccountsData(data);
      this.vm.account = this.accounts; // Also set vm.account for the modal component
      // console.log('accounts with history', this.accounts);
      this.checkLoadingComplete();
    }).then();

    // Load reference field types reporting_field_type?state=active&pageNumber=0&pageSize=99999
    this.getTableData('reporting_field_type', '?state=active', (data) => {
      this.rfType = data;
      // console.log('rfType', this.rfType);
      this.loadReferenceFields();
      this.checkLoadingComplete();
      this.isLoading = false;
    }).then();

    this.getTableData('reporting_field', '?state=active', (data) => {
      this.rfValues = data;
      // console.log('rfValues', this.rfValues);
      // Only build picklists if types are loaded too
      this.loadReferenceFields();
      this.checkLoadingComplete();
    }).then();
  }


  checkLoadingComplete(): void {
    // Check if all essential data is loaded
    if (this.companies && this.locations && this.startDates && this.rfType && this.rfValues && this.accounts && this.currencies) {
      this.isLoading = false;
    }
  }

  loadReferenceFields(): void {
    // We need both lists to proceed
    if (!Array.isArray(this.rfType) || !Array.isArray(this.rfValues)) {
      return;
    }

    // Clear existing lists (optional but avoids stale data)
    this.rf1 = [];
    this.rf2 = [];
    this.rf3 = [];
    this.rf4 = [];
    this.rf5 = [];

    // We assume the first 5 active rfType entries map to rf1..rf5 slots in order
    // If your rfType items carry their own slot, replace `slot` logic accordingly.
    const activeTypes = this.rfType.filter(t => !!(t?.active));

    for (let i = 0; i < Math.min(activeTypes.length, 5); i++) {
      const type = activeTypes[i];
      const slot = i + 1; // 1..5 picklists in order
      const typeIdStr = String(type?.id ?? slot).trim(); // prefer explicit id if present

      const items = this.rfValues.filter((v: any) => {
        const vTypeId = String(v?.type_id ?? '').trim();
        const matchesType = vTypeId === typeIdStr;
        // Treat values as active if `active` not present; adapt if your API uses `state: 'active'`
        const isActive = v?.active !== false && (v?.state ? String(v.state).toLowerCase() === 'active' : true);
        return matchesType && isActive;
      });

      switch (slot) {
        case 1:
          this.rf1 = items;
          break;
        case 2:
          this.rf2 = items;
          break;
        case 3:
          this.rf3 = items;
          break;
        case 4:
          this.rf4 = items;
          break;
        case 5:
          this.rf5 = items;
          break;
      }
    }
    // console.log('rf1..rf5', this.rf1, this.rf2, this.rf3, this.rf4, this.rf5);
  }

  initNewBudget(): void {
    this.isLoading = true;

    // Base date for month labels: first day of current year
    const yearStart = new Date(new Date().getFullYear(), 0, 1);

    // Build an empty budget document locally
    this.budget = {
      header: {
        id: '',                 // No id until saved (API will assign)
        number: '',
        description: '',
        company_id: undefined,
        location_id: undefined,
        currency_id: undefined,
        start_date: '',         // used for period calculations and headings
        header_date: new Date() as any,
        rf_id_1: undefined,
        rf_id_2: undefined,
        rf_id_3: undefined,
        rf_id_4: undefined,
        rf_id_5: undefined,
        fiscal_year: undefined,
        status: undefined,
        created_by: undefined,
        created_at: undefined,
        updated_at: undefined,
      } as BudgetHeader,
      detail: [],               // start with no rows
      netIncome: {              // zeroed
        history: 0,
        m1: 0, m2: 0, m3: 0, m4: 0, m5: 0, m6: 0,
        m7: 0, m8: 0, m9: 0, m10: 0, m11: 0, m12: 0
      }
    } as any;

    // UI defaults
    this.budgetTypeName = 'Regular';
    this.budgetTypeClass = 'budget-regular';
    this.total = 0;

    // If user later selects a start date, this updates header_date
    if (this.budget.header.start_date) {
      this.onDateChange();
    }

    // Establish baseline for Unsaved Changes guard
    this.originalDocument = structuredClone(this.budget);

    // Done
    this.checkLoadingComplete();
    this.addBudgetDetailRow();
    this.isLoading = false;
  }

  loadBudget(id: string): void {
    this.isLoading = true;
    this.showBudget = true; // if we already have a saved budget lets open to that panel
    // console.log('loadBudget id: ', id);
    // Fetch a single budget from the API
    this.dataService.getData('budget', String(id)).subscribe({
      next: (result: any) => {
        // Some endpoints return { Data: ... }, others return the object directly
        const data = (result && result.Data) ? result.Data : result;
        // console.log('loadBudget data', data);

        this.budget = data as any;

        // Sort detail rows by sequence if provided
        if (Array.isArray(this.budget.detail)) {
          this.budget.detail.sort((a: any, b: any) => (Number(a?.sequence ?? 0)) - (Number(b?.sequence ?? 0)));
        }

        // Normalize stored monthly actuals on each detail row (now use m1..m12 for Actuals)
        if (Array.isArray(this.budget.detail)) {
          for (const r of this.budget.detail) {
            // Coerce total history
            if (r.history !== undefined && r.history !== null) {
              r.history = Number(r.history) || 0;
            } else {
              r.history = Number(r.history) || 0;
            }

            let monthlyProvided = false;
            for (let i = 1; i <= 12; i++) {
              const key = `m${i}`;
              if (r[key] !== undefined && r[key] !== null && r[key] !== '') {
                r[key] = Number(r[key]) || 0;
                if (r[key] !== 0) {
                  monthlyProvided = true;
                }
                continue;
              }
              const altKeys = [
                `actual_m${i}`
              ];
              const found = altKeys.find(k => r[k] !== undefined && r[k] !== null && r[k] !== '');
              if (found) {
                r[key] = Number(r[found]) || 0;
                if (r[key] !== 0) {
                  monthlyProvided = true;
                }
              } else {
                r[key] = 0;
              }
            }
          }
        }

        // Backfill missing account_id on legacy rows using currently loaded accounts
        if (Array.isArray(this.budget.detail) && Array.isArray(this.accounts) && this.accounts.length) {
          for (const r of this.budget.detail) {
            if (!r.account_id) {
              // Prefer match by account number; fallback to name
              const acc = this.accounts.find(a => a.number === r.number) || this.accounts.find(a => a.name === r.name);
              if (acc) {
                r.account_id = acc.id;
              }
            }
          }
        }

        // Existing stored rows should not trigger account-sum fetch
        if (Array.isArray(this.budget.detail)) {
          this.budget.detail.forEach((r: any) => {
            if ('isNewLine' in r) {
              delete r.isNewLine;
            }
          });
        }

        // Ensure unique IDs for detail rows (protects row operations like delete/spread)
        this.normalizeDetailIds();

        // Recompute totals and set type label/color
        this.calculateTotal();
        this.setBudgetType();

        // Update month heading labels from the selected start date
        if (this.budget?.header?.start_date) {
          this.onDateChange();
        }

        // Establish baseline for Unsaved Changes guard
        this.originalDocument = structuredClone(this.budget);

        // UI state
        this.checkLoadingComplete();
      },
      error: () => {
        this.notificationService.showErrorToast('Failed to load budget');
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  emailSummaryTable() {
    // Ensure the budget tab is visible so the DOM for the summary is present
    const originalShowBudgetState = this.showBudget;
    if (!this.showBudget) {
      this.showBudget = true;
    }

    // Allow DOM to render before capturing HTML
    setTimeout(() => {
      const reportName = 'Budget Summary';
      const emailDocObj = {} as EmailModel;
      // report formatting
      emailDocObj.print_orientation = '2'; // 1=portrait, 2=landscape
      emailDocObj.convert_attachment_to = 'pdf'; // convert pdfhtml to pdf or leave as html

      // Build subject/body
      const header = this.budget?.header || {} as BudgetHeader;
      const docNumber = header.number ? ` ${header.number}` : '';
      const desc = header.description ? ` - ${header.description}` : '';
      const title = `${this.budgetTypeName} Budget${docNumber}${desc}`.trim();

      emailDocObj.fromEmail = '';
      emailDocObj.fromName = 'AcctXErp';
      emailDocObj.header_id = header.id || 0;
      emailDocObj.toEmail = '';
      emailDocObj.Subject = `AcctXErp ${reportName}: ${title}`;
      emailDocObj.body = `Please see the attached ${reportName} for${docNumber || ''}${desc || ''}.` +
        '<br><br><br>Best regards,<br><br>AcctXErp<br><br>';

      // Use the common buildPdfHtml method for consistent styling
      try {
        const container = this.summaryTable?.nativeElement as HTMLElement;
        if (container) {
          // Use centralized helper method
          const dateRange = `${this.budget.header.start_date || ''} to ${this.budgetEndDate || ''}`;
          const extraMeta = [
            {label: 'Company', value: this.getCompanyName()},
            {label: 'Location', value: this.getLocationName()},
            {label: 'Description', value: this.budget.header.description}
          ];
          emailDocObj.pdfHtml = this.helperService.buildPdfHtml(container.innerHTML, title, dateRange, extraMeta);
        }
      } catch (e) {
        console.error('Error building PDF HTML for email:', e);
      }

      emailDocObj.activity_message = reportName;
      this.reportDataToSendInEmail = emailDocObj;

      // Show the shared Send Email modal
      const element = document.getElementById('sendEmailModal') as HTMLElement;
      if (element) {
        const emailModal = new Modal(element);
        emailModal.show();
      }

      // Restore the original state of showBudget after a brief delay
      setTimeout(() => {
        this.showBudget = originalShowBudgetState;
      }, 500);
    }, 250);
  }

  calculateTotal(): void {
    // Calculate total from budget.detail instead of budget.items
    this.total = this.budget.detail?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
  }

  setBudgetType(): void {
    if (this.total < 0) {
      this.budgetTypeName = 'Interim';
      this.budgetTypeClass = 'budget-interim';
    } else {
      this.budgetTypeName = 'Regular';
      this.budgetTypeClass = 'budget-regular';
    }
  }

  updateActualsDialog() {
    const messages: string[] = [
      $localize`:Are you sure you want to update Actuals? @@areYouSureYouWantToUpdateActuals:Are you sure you want to update Actuals?`,
      this.budget?.header?.number || '',
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.updateActuals();
      },
      () => {
        this.notificationService.showSuccessToast(
          $localize`:Update Canceled @@updateCanceled:Update Canceled`
        );
      }
    );
  }

  addAllAccounts(): void {
    const messages: string[] = [
      `Are you sure you want to insert ${this.accounts.length - this.budget.detail.length} more accounts into this budget?`,
      `${this.budget?.header?.number || ''} ${this.budget?.header?.description || ''}`.trim()
    ];

    this.confirmDialogService.confirmThis(
      messages,
      () => {
        // On confirm
        if (!Array.isArray(this.accounts) || this.accounts.length === 0) {
          this.notificationService.showWarningToast('No accounts available to add');
          return;
        }
        // Determine allowed account types
        const allowed = new Set(['revenue', 'expenses', 'costs']);
        // Existing accounts already on the budget (by account_id)
        const existing = new Set<string>(
          (this.budget?.detail || [])
            .filter(r => r && r.account_id)
            .map(r => String(r.account_id))
        );

        // Build list of accounts to add
        const toAdd = this.accounts.filter(a => allowed.has(String(a.type_name || '').toLowerCase()) && !existing.has(String(a.id)));

        if (!toAdd.length) {
          this.notificationService.showSuccessToast('All eligible accounts are already in this budget');
          return;
        }

        // Insert rows for each account not already present
        for (const account of toAdd) {
          const newRow: any = {
            isNewLine: true,
            id: (Date.now() + Math.random()).toString(),
            header_id: this.budget?.header?.id,
            account_id: account.id,
            number: account.number,
            name: account.name,
            type_name: account.type_name,
            amount: 0,
            history: Number(account.history || 0),
            spread: '',
            m1: 0, m2: 0, m3: 0, m4: 0, m5: 0, m6: 0,
            m7: 0, m8: 0, m9: 0, m10: 0, m11: 0, m12: 0
          };
          this.budget.detail.push(newRow);
        }

        // After insertion, optionally fetch actuals for all newly added accounts using array GET
        if (this.budget?.header?.start_date) {
          const sd = new Date(this.budget.header.start_date as any);
          const startDate = sd.toISOString();
          const endDate = this.addMonthsToDate(sd, 12).toISOString();

          // Only request actuals for the newly inserted account IDs
          const ids = toAdd.map(a => String(a.id));
          let httpParams = new HttpParams();
          ids.forEach(id => httpParams = httpParams.append('accountId', id));
          httpParams = httpParams.set('startDate', startDate);
          httpParams = httpParams.set('endDate', endDate);
          httpParams = httpParams.set('signed', 'false');

          this.isSubmitting = true;
          this.dataService.getDataByArray('budget/account-sum', httpParams).subscribe({
            next: (res: any) => {
              const data = (res && res.Data) ? res.Data : res;
              // console.log('addAllAccounts data', data);
              if (Array.isArray(data)) {
                this.applyMonthlyActuals(data);
              } else if (data && typeof data === 'object' && Array.isArray(data.accounts)) {
                this.applyMonthlyActuals(data.accounts);
              } else {
                const maybeArray = data ? Object.values(data) : [];
                if (Array.isArray(maybeArray)) {
                  this.applyMonthlyActuals(maybeArray as any);
                }
              }
              this.calcTotal();
              this.notificationService.showSuccessToast(`${toAdd.length} account(s) added`);
            },
            error: () => {
              this.notificationService.showErrorToast('Failed to retrieve actuals for inserted accounts');
            },
            complete: () => {
              this.isSubmitting = false;
            }
          });
        } else {
          // No start date; just recalc totals
          this.calcTotal();
          this.notificationService.showSuccessToast(`${toAdd.length} account(s) added`);
        }
      },
      () => {
        // Cancel
        this.notificationService.showSuccessToast('Insert Canceled');
      }
    );
  }

  copyBudget(): void {
    const messages: string[] = [
      'Are you sure you want to copy this budget?',
      `${this.budget?.header?.description} to a new Budget named - Copy of ${this.budget?.header?.description}`.trim()
    ];

    this.confirmDialogService.confirmThis(
      messages,
      () => {
        try {
          // Build a payload from the current budget
          const payload = this.buildApiPayload(this.budget);

          // Ensure it becomes a new budget on the server
          if (payload?.header) {
            payload.header.id = '';
            // Clear number to avoid conflicts if server enforces uniqueness
            payload.header.number = '';
            // Prefix description
            const desc = this.budget?.header?.description || '';
            payload.header.description = desc.startsWith('Copy of') ? desc : `Copy of ${desc}`;
            // Reset initiation date to now
            payload.header.header_date = new Date() as any;
          }

          // Remove IDs from detail rows so server can create new ones
          if (Array.isArray(payload?.detail)) {
            payload.detail = payload.detail.map((r: any) => {
              const x = {...r};
              delete x.id;
              delete x.header_id;
              return x;
            });
          }

          this.isSubmitting = true;
          this.dataService.post('budget/save', payload).subscribe({
            next: (result: any) => {
              const saved = (result && result.Data) ? result.Data : result;
              if (saved) {
                this.budget = saved;
                this.normalizeDetailIds();
                if (this.budget?.header?.start_date) {
                  this.onDateChange();
                }
                this.showBudget = true;
                this.calculateTotal();
                this.setBudgetType();
                this.originalDocument = structuredClone(this.budget);
                this.notificationService.showSuccessToast('Budget copied successfully');
              } else {
                this.notificationService.showSuccessToast('Budget copied');
              }
            },
            error: (err) => {
              console.error('Copy budget failed', err);
              this.notificationService.showErrorToast('Failed to copy budget');
            },
            complete: () => {
              this.isSubmitting = false;
            }
          });
        } catch (e) {
          console.error('Copy budget exception', e);
          this.notificationService.showErrorToast('Unexpected error while copying budget');
          this.isSubmitting = false;
        }
      },
      () => {
        this.notificationService.showSuccessToast('Copy Canceled');
      }
    );
  }

  updateActuals(): void {
    // Bulk update actuals for all rows using account-sum endpoint
    try {
      this.isLoading = true;
      if (!this.budget?.header?.start_date) {
        this.notificationService.showWarningToast('Select a Start Date before updating actuals');
        return;
      }
      if (!Array.isArray(this.budget?.detail) || this.budget.detail.length === 0) {
        this.notificationService.showWarningToast('No budget rows to update');
        return;
      }

      // Collect accounts to query
      const sd = new Date(this.budget.header.start_date as any);
      const startDate = sd.toISOString();
      const endDate = this.addMonthsToDate(sd, 12).toISOString();

      const requests = this.budget.detail
        .filter(r => !!r.account_id)
        .map(r => ({
          accountId: String(r.account_id),
          startDate,
          endDate,
          signed: false
        }));

      if (!requests.length) {
        this.notificationService.showWarningToast('No accounts found on budget rows');
        return;
      }

      // Build query params per API: [FromQuery] List<string> accountId, startDate, endDate, signed
      let httpParams = new HttpParams();
      // accountId should be repeated for each account id in the query string
      requests.forEach(r => {
        httpParams = httpParams.append('accountId', r.accountId);
      });
      httpParams = httpParams.set('startDate', startDate);
      httpParams = httpParams.set('endDate', endDate);
      httpParams = httpParams.set('signed', 'false');

      // console.log('updateActuals requests', httpParams.toString());
      this.isSubmitting = true;
      this.dataService.getDataByArray('budget/account-sum', httpParams).subscribe({
        next: (res: any) => {
          const data = (res && res.Data) ? res.Data : res;
          // console.log('updateActuals data', data);
          if (Array.isArray(data)) {
            // Apply monthly actuals to matching rows by account_id
            this.applyMonthlyActuals(data);
          } else if (data && typeof data === 'object' && Array.isArray(data.accounts)) {
            // Some APIs may wrap in { accounts: [...] }
            this.applyMonthlyActuals(data.accounts);
          } else {
            // If the API returns a map keyed by account_id
            const maybeArray = data ? Object.values(data) : [];
            if (Array.isArray(maybeArray)) {
              this.applyMonthlyActuals(maybeArray as any);
            }
          }
          this.notificationService.showSuccessToast('Actuals updated');
          this.calcTotal();
        },
        error: (err) => {
          console.error('Update actuals failed', err);
          this.notificationService.showErrorToast('Failed to update actuals');
          this.isLoading = false;
        },
        complete: () => {
          this.isSubmitting = false;
          this.isLoading = false;
        }
      });
    } catch (e) {
      this.isSubmitting = false;
      this.isLoading = false;
      this.notificationService.showErrorToast('Unexpected error while updating actuals');
    }
  }

  save(): void {
    const valid = this.validateForm();

    if (!valid) {
      return;
    }

    this.isLoading = true;
    this.isSubmitting = true;

    // Check for duplicate Locked budgets for the same company/location and overlapping period
    // If the budget was already Locked when opened, we skip this check as per requirements.
    if (this.budget.header.status === 'Locked' && this.originalDocument?.header?.status !== 'Locked') {
      const companyId = this.budget.header.company_id;
      const locationId = this.budget.header.location_id;
      const startDate = new Date(this.budget.header.start_date);
      const endDate = this.addMonthsToDate(startDate, 12);

      const params = `?company_id=${companyId}&location_id=${locationId}&status=Locked`;

      this.dataService.getData('budget', params).subscribe({
        next: (result: any) => {
          const existingBudgets = (result && result.Data) ? result.Data : (Array.isArray(result) ? result : []);

          const overlap = existingBudgets.find((b: any) => {
            // Skip the current budget being edited
            if (this.budget.header.id && String(b.id) === String(this.budget.header.id)) {
              return false;
            }

            const bStart = new Date(b.start_date);
            const bEnd = this.addMonthsToDate(bStart, 12);

            // Overlap logic: (StartA < EndB) and (EndA > StartB)
            return startDate < bEnd && endDate > bStart;
          });

          if (overlap) {
            this.notificationService.showWarningToast('A Locked budget already exists for this company, location, and period. Periods cannot overlap.');
            this.isLoading = false;
            this.isSubmitting = false;
            return;
          }

          this.executeSave();
        },
        error: (err) => {
          console.error('Error checking for existing budgets:', err);
          // Proceed with save if check fails, or handle as error?
          // Better to block if we can't verify uniqueness when status is Locked.
          this.notificationService.showErrorToast('Error verifying budget uniqueness. Please try again.');
          this.isLoading = false;
          this.isSubmitting = false;
        }
      });
    } else {
      this.executeSave();
    }
  }

  private executeSave(): void {
    const payload = this.buildApiPayload(this.budget);
    // Create vs Update based on header.id
    const isUpdate = !!this.budget?.header?.id;
    const idStr = isUpdate ? String(this.budget.header.id) : '';

    const obs = isUpdate
      ? this.dataService.put('budget', idStr, payload)
      : this.dataService.post('budget/save', payload);

    obs.subscribe({
      next: (result: any) => {
        // Normalize API response (some endpoints return { Data: ... })
        const saved = (result && result.Data) ? result.Data : result;

        this.budget = saved || this.budget; // fall back if server returns nothing
        this.isSubmitting = false;
        // this.notificationService.showSuccessToast('Budget saved successfully');
        this.showBudget = true;
        this.calculateTotal();

        // Update originalDocument after successful save to prevent unsaved changes warning
        this.originalDocument = structuredClone(this.budget);
      },
      error: (err) => {
        this.notificationService.showErrorToast('Failed to save budget');
        this.isSubmitting = false;
        console.error(err);
      },
      complete: () => {
        if (this.saveClose) {
          this.router.navigateByUrl('/admin/budget-list').then();
        }
        this.isLoading = false;
      }
    });
  }

  // Build API payload: strip fields not to send and computed summaries
  private buildApiPayload(budget: any): any {
    const clone = structuredClone(budget);
    if (Array.isArray(clone.detail)) {
      clone.detail = clone.detail.map((row: any, idx: number) => {
        const r = {...row};
        r.sequence = idx + 1; // server can use this to store order
        // existing month coercion, etc.
        for (let i = 1; i <= 12; i++) {
          r[`m${i}`] = Number(r[`m${i}`] ?? 0) || 0;
          const sKey = `m${i}spread`;
          if (sKey in r) {
            r[sKey] = Number(r[sKey] ?? 0) || 0;
          }
        }
        delete r.header_id;
        return r;
      });
    }
    // ...
    return clone;
  }

  validateForm(fieldsOnly = false): boolean {
    let valid = true;
    let dataValid = true;

    this.companyError = false;
    this.locationError = false;
    this.dateMissing = false;
    this.descriptionError = false;
    this.currencyError = false;

    if (!this.budget.header.company_id) {
      this.companyError = true;
      valid = false;
    }

    if (!this.budget.header.location_id) {
      this.locationError = true;
      valid = false;
    }

    if (!this.budget.header.start_date) {
      this.dateMissing = true;
      valid = false;
    }

    if (!this.budget.header.description) {
      this.descriptionError = true;
      valid = false;
    }

    if (!this.budget.header.currency_id) {
      this.currencyError = true;
      valid = false;
    }

    if (!this.budget.header.status) {
      this.statusError = true;
      valid = false;
    }

    // check for budget data
    let total = 0;

    if (!fieldsOnly) {
      this.budget.detail.forEach(item => {
        // Loop through each month from m1 to m12
        for (let i = 1; i <= 12; i++) {
          const monthField = `m${i}`;
          const monthValue = parseFloat(item[monthField]);
          const monthSpreadField = `m${i}spread`;
          const monthSpreadValue = parseFloat(item[monthSpreadField]);
          if (!isNaN(monthValue)) {
            total += monthValue;
          }
          if (!isNaN(monthSpreadValue)) {
            total += monthSpreadValue;
          }
        }
        // console.log('total', total);
      });

      if (total == 0) {
        this.budgetDataMissing = true;
        dataValid = false;
      }

      // New: block save if any Default-mode row mismatches
      const invalidRows: string[] = [];
      for (const row of this.budget.detail) {
        if (this.rowAmountMismatch(row)) {
          invalidRows.push(`${row.number} ${row.name}`.trim());
        }
      }
      if (invalidRows.length) {
        valid = false;
        this.notificationService.showWarningToast(
          `Month sums must equal Amount on rows with account number: ${invalidRows.slice(0, 5).join('; ')}${invalidRows.length > 5 ? '...' : ''}`
        );
      }
    }

    if (!valid) {
      this.notificationService.showWarningToast('Please complete all required fields');
    }

    if (!dataValid) {
      this.notificationService.showWarningToast('Budget has no data. Must have at least one budget item.');
      valid = false;

    }
    return valid;
  }

  /**
   * Print the budget
   */
  print(): void {
    window.print();
  }

  ngOnDestroy(): void {
    // No special cleanup required for CDK drag-drop
  }

  // list button handlers
  onSaveNewFromButtons(): void {
    this.save();
    // after save, init a new budget
    this.router.navigateByUrl(`/admin/budget-form?id=`).then();
  }

  onSaveCloseFromButtons(): void {
    this.saveClose = true;
    this.save();
  }

  onDeleteFromButtons(): void {
    if (!this.budget?.header?.id) {
      return;
    }
    const idStr = String(this.budget.header.id);
    const messages: string[] = ['Delete this Budget?', this.budget.header.number || ''];
    this.confirmDialog(messages, () => {
      this.dataService.deleteDataById('budget', idStr).subscribe({
        next: () => {
          // this.notificationService.showSuccessToast('Budget deleted');
          this.router.navigateByUrl('/admin/budget-list').then();
        },
        error: () => this.notificationService.showErrorToast('Failed to delete budget')
      });
    });
  }

  onResetFromButtons(): void {
    if (this.budget?.header?.id) {
      this.notificationService.showWarningToast('Previously saved budget cannot be reset.');
      return;
    } else if (!this.budget?.header?.id) {
      this.budget = structuredClone(this.originalDocument);
      this.calcTotal();
    }

  }

  onCloseFromButtons(): void {
    this.router.navigateByUrl('/admin/budget-list').then();
  }

  private confirmDialog(messages: string[], yes: () => void): void {
    this.confirmDialogService.confirmThis(messages, yes, () => {
      this.notificationService.showSuccessToast('Delete Canceled');
    });
  }

  /**
   * Handle changes in company selection
   */
  onCompanyChange(): void {
    // Additional logic when company changes, if needed
    this.companyError = false;
  }

  /**
   * Handle changes in location selection
   */
  onLocationChange(): void {
    // Additional logic when location changes, if needed
    this.locationError = false;
  }

  /**
   * Handle changes in date selection
   */
  onDateChange(): void {
    // Clear the date missing error flag
    this.dateMissing = false;
    this.setBudgetStartEnd();
    // No mutation of header_date here; month headings and periods derive from start_date
  }

  /**
   * see start end dates for display
   */
  setBudgetStartEnd(): void {
    if (!this.budget?.header?.start_date) {
      return;
    }

    // Normalize start as Date at local midnight
    const start = new Date(this.budget?.header?.start_date);
    start.setHours(0, 0, 0, 0);

    // End = start + 12 months (exclusive) — same as used in updateActuals
    const end = this.addMonthsToDate(start, 12);

    // Option A: keep as Date objects for internal logic
    this.budgetStartDate = start;
    this.budgetEndDate = end;
  }

  // Helper methods to get accounts by type
  private isRevenueRow(item: any): boolean {
    const t = String(item?.type_name ?? '').trim().toLowerCase();
    return t === 'revenue' || t === 'revenues' || t === 'income';
  }

  getRevenueAccounts(): any[] {
    if (!this.budget || !this.budget.detail) {
      return [];
    }
    return this.budget.detail.filter(item => this.isRevenueRow(item));
  }

  getCostOfGoodsSoldAccounts(): any[] {
    if (!this.budget || !this.budget.detail) {
      return [];
    }
    return this.budget.detail.filter(item =>
      item.type_name === 'Costs' ||
      ((item.type_name === 'Expenses' || item.type_name === 'Expense') &&
        item.name.toLowerCase().includes('cost of goods'))
    );
  }

  getOperatingExpensesAccounts(): any[] {
    if (!this.budget || !this.budget.detail) {
      return [];
    }
    return this.budget.detail.filter(item =>
      (item.type_name === 'Expenses' || item.type_name === 'Expense') &&
      !item.name.toLowerCase().includes('cost of goods')
    );
  }

  // Helper methods for summary table calculations
  getRevenueActual(): number {
    return this.getRevenueAccounts().reduce((sum, item) => sum + Number(item.history || 0), 0);
  }

  getRevenueBudget(): number {
    return this.getRevenueAccounts().reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }

  getRevenueMonthBudget(month: number): number {
    const monthKey = `m${month}spread` as keyof typeof this.budget.detail[0];
    return this.getRevenueAccounts().reduce((sum, item) => sum + Number(item[monthKey] || 0), 0);
  }

  // Actual values by month are derived from per-row actuals (m1..m12)
  getRevenueMonthActual(month: number): number {
    const key = `m${month}` as const;
    return this.getRevenueAccounts().reduce((sum, item) => sum + Number((item as any)[key] || 0), 0);
  }

  getCostOfGoodsSoldActual(): number {
    return this.getCostOfGoodsSoldAccounts().reduce((sum, item) => sum + Number(item.history || 0), 0);
  }

  getCostOfGoodsSoldBudget(): number {
    return this.getCostOfGoodsSoldAccounts().reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }

  getCostOfGoodsSoldMonth(month: number): number {
    const monthKey = `m${month}spread` as keyof typeof this.budget.detail[0];
    return this.getCostOfGoodsSoldAccounts().reduce((sum, item) => sum + Number(item[monthKey] || 0), 0);
  }

  // Actual values by month come from per-row actuals (m1..m12)
  getCostOfGoodsSoldMonthActual(month: number): number {
    const key = `m${month}` as const;
    return this.getCostOfGoodsSoldAccounts().reduce((sum, item) => sum + Number((item as any)[key] || 0), 0);
  }

  getGrossMarginActual(): number {
    return this.getRevenueActual() - this.getCostOfGoodsSoldActual();
  }

  getGrossMarginBudget(): number {
    return this.getRevenueBudget() - this.getCostOfGoodsSoldBudget();
  }

  getGrossMarginMonth(month: number): number {
    return this.getRevenueMonthBudget(month) - this.getCostOfGoodsSoldMonth(month);
  }

  // New methods for actual values
  getGrossMarginMonthActual(month: number): number {
    return this.getRevenueMonthActual(month) - this.getCostOfGoodsSoldMonthActual(month);
  }

  getOperatingExpensesActual(): number {
    return this.getOperatingExpensesAccounts().reduce((sum, item) => sum + Number(item.history || 0), 0);
  }

  getOperatingExpensesBudget(): number {
    return this.getOperatingExpensesAccounts().reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }

  getOperatingExpensesMonth(month: number): number {
    const monthKey = `m${month}spread` as keyof typeof this.budget.detail[0];
    return this.getOperatingExpensesAccounts().reduce((sum, item) => sum + Number(item[monthKey] || 0), 0);
  }

  // Actual values by month come from per-row actuals (m1..m12)
  getOperatingExpensesMonthActual(month: number): number {
    const key = `m${month}` as const;
    return this.getOperatingExpensesAccounts().reduce((sum, item) => sum + Number((item as any)[key] || 0), 0);
  }

  getNetIncomeActual(): number {
    return this.getGrossMarginActual() - this.getOperatingExpensesActual();
  }

  getNetIncomeBudget(): number {
    return this.getGrossMarginBudget() - this.getOperatingExpensesBudget();
  }

  getNetIncomeMonth(month: number): number {
    return this.getGrossMarginMonth(month) - this.getOperatingExpensesMonth(month);
  }

  // Actual values by month for net income derived from per-row actuals
  getNetIncomeMonthActual(month: number): number {
    return this.getGrossMarginMonthActual(month) - this.getOperatingExpensesMonthActual(month);
  }

  // Compute month label from start_date + offset
  getMonthLabel(offset: number): string {
    const base = this.budget?.header?.start_date ? new Date(this.budget.header.start_date as any) : new Date();
    const d = new Date(base);
    d.setMonth(base.getMonth() + offset);
    return d.toLocaleString(undefined, {month: 'short'});
  }

  private normalizeDetailIds(): void {
    if (!this.budget?.detail) {
      return;
    }
    const used = new Set<string>();
    for (const row of this.budget.detail) {
      let rid = row?.id;
      if (!rid || rid === '') {
        rid = (Date.now() + Math.random()).toString();
      }
      // If duplicate, regenerate until unique in this collection
      while (used.has(String(rid))) {
        rid = (Date.now() + Math.random()).toString();
      }
      row.id = String(rid);
      used.add(String(rid));
    }
  }

  // Helper methods for individual account values
  getAccountActual(account: any): number {
    return Number(account.history || 0);
  }

  getAccountBudget(account: any): number {
    return Number(account.amount || 0);
  }

  getAccountMonth(account: any, month: number): number {
    const monthKey = `m${month}spread` as keyof typeof account;
    return Number(account[monthKey] || 0);
  }

  // Actual monthly value stored on the row as m{n}
  getAccountMonthActual(account: any, month: number): number {
    const key = `m${month}` as keyof typeof account;
    return Number(account[key] || 0);
  }

  private sumMonths(row: any): number {
    let s = 0;
    for (let i = 1; i <= 12; i++) {
      s += Number(row[`m${i}`]) || 0;
    }
    return s;
  }

  private sumMonthsBudget(row: any): number {
    let s = 0;
    for (let i = 1; i <= 12; i++) {
      s += Number(row[`m${i}spread`]) || 0;
    }
    return s;
  }

  // Helper for template: determines if per-month diff exceeds rounding tolerance
  monthDiffExceeds(row: any, monthKey: string): boolean {
    const v = Number(row?.[monthKey] ?? 0);
    const s = Number(row?.[`${monthKey}spread`] ?? 0);
    const tolerance = 1; // allow 1-unit rounding difference
    if (!row || row.spread === 'Detail') {
      return false;
    }
    // if no spread baseline, don't warn
    if (row?.[`${monthKey}spread`] === undefined || row?.[`${monthKey}spread`] === '') {
      return false;
    }
    return Math.abs(v - s) > tolerance;
  }

  rowAmountMismatch(row: any): boolean {
    if (!row || !row.spread || row.spread === 'Detail') {
      return false;
    }
    const sum = this.sumMonthsBudget(row);
    const amount = Number(row.amount) || 0;
    const tolerance = 1; // allow small rounding tolerance
    return Math.abs(sum - amount) > tolerance;
  }

  trackByDetailId(index: number, item: any) {
    return item?.id ?? index;
  }

  // Handle row drop with Angular CDK to reorder rows and update sequence
  onDropRow(event: CdkDragDrop<any[]>): void {
    if (!this.budget?.detail || !Array.isArray(this.budget.detail)) {
      return;
    }
    moveItemInArray(this.budget.detail, event.previousIndex, event.currentIndex);
    this.budget.detail.forEach((r: any, idx: number) => r.sequence = idx + 1);
    this.calcTotal();
  }

  // Called when a month cell loses focus to trigger recalculation and re-spread Rev-based rows on the fly
  onMonthValueChanged(row: any): void {
    // Ensure numeric coercion to avoid accidental strings on spread fields (UI binds to mXspread)
    for (let i = 1; i <= 12; i++) {
      const skey = `m${i}spread` as keyof typeof row;
      row[skey] = Number(row[skey] || 0);
    }
    // If user selected Detail, back-calc amount from spread months (what the user edits)
    if (row.spread === 'Detail') {
      row.amount = this.sumMonthsBudget(row);
    }
    this.calcTotal();
  }

  // Allocate amount across 12 months by pct ensuring rounded parts sum exactly to amount
  private allocateByPct(amount: number, pct: number[]): number[] {
    const amt = Math.round(Number(amount) || 0);
    if (!amt || !pct?.length) {
      return Array(12).fill(0);
    }
    const p = pct.map(v => Number(v) || 0);
    const sumP = p.reduce((s, v) => s + v, 0);
    if (sumP === 0) {
      return Array(12).fill(0);
    }
    // ideal shares
    const ideal = p.map(v => amt * (v / sumP));
    const floors = ideal.map(v => Math.floor(v));
    let remainder = amt - floors.reduce((s, v) => s + v, 0);
    // Distribute the remaining units to the largest fractional parts
    const fracIdx = ideal.map((v, i) => ({i, f: v - Math.floor(v)}))
      .sort((a, b) => b.f - a.f)
      .map(x => x.i);
    const result = floors.slice();
    let idx = 0;
    while (remainder > 0 && idx < fracIdx.length) {
      result[fracIdx[idx]] += 1;
      remainder--;
      idx++;
      if (idx >= fracIdx.length) {
        idx = 0;
      }
    }
    return result;
  }

  private async fetchLyForRow(row: any): Promise<void> {
    if (!row?.account_id) {
      this.notificationService.showWarningToast('Select an account before using LY spread');
      return;
    }
    if (!this.budget?.header?.start_date) {
      this.notificationService.showWarningToast('Select a Start Date before using LY spread');
      return;
    }

    // Budget’s start (for clarity only)
    const budgetStart = new Date(this.budget.header.start_date as any);

    // LY window is one year prior to budget start
    const windowStart = this.addMonthsToDate(budgetStart, -12);
    const windowEnd = this.addMonthsToDate(windowStart, 12);

    const toYmd = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const params = `?accountId=${encodeURIComponent(String(row.account_id))}`
      + `&startDate=${toYmd(windowStart)}`
      + `&endDate=${toYmd(windowEnd)}`
      + `&signed=false`;

    return new Promise<void>((resolve) => {
      this.dataService.getData('budget/account-sum', params).subscribe({
        next: (res: any) => {
          const data = (res && res.Data) ? res.Data : res;
          let target: any = null;
          if (Array.isArray(data)) {
            target = data.find((a: any) => String(a.account_id) === String(row.account_id));
          } else if (data && typeof data === 'object' && data.months) {
            target = data;
          }
          if (!target || !Array.isArray(target.months)) {
            this.notificationService.showWarningToast('No LY monthly data found for this account');
            resolve();
            return;
          }

          // Clear existing budget spread months only (do not touch Actuals m1..m12)
          for (let i = 1; i <= 12; i++) {
            row[`m${i}spread`] = 0;
          }

          // Map months to 1..12 relative to windowStart (not budgetStart)
          const monthIndex = (y: number, month1to12: number): number => {
            const d = new Date(y, month1to12 - 1, 1);
            const monthsDiff = (d.getFullYear() - windowStart.getFullYear()) * 12
              + (d.getMonth() - windowStart.getMonth());
            return monthsDiff + 1; // 1..12 inside the window
          };

          for (const m of target.months) {
            const idx = monthIndex(Number(m.year), Number(m.month));
            if (idx >= 1 && idx <= 12) {
              const v = Number(m.debit_credit) || 0;
              row[`m${idx}spread`] = v;
            }
          }

          // Make the row amount equal to the LY spread months sum
          row.amount = this.sumMonthsBudget(row);

          resolve();
        },
        error: () => {
          this.notificationService.showErrorToast('Failed to load LY data');
          resolve();
        }
      });
    });
  }

  spread(id: string, spread: string): void {
    let clear = false;

    for (let x = 0; x < this.budget.detail.length; x++) {
      if (this.budget.detail[x].id === id) {
        const row = this.budget.detail[x];

        // Forbid Rev for Revenue rows
        if (spread === 'Rev' && row.type_name === 'Revenue') {
          this.notificationService.showWarningToast('Revenue rows cannot use Rev spread');
          row.spread = '';
          return;
        }

        if (spread === 'SL') {
          if (!Number(row.amount)) {
            clear = true;
          } else {
            // Allocate evenly across 12 months but ensure sum equals amount
            const pct = Array(12).fill(1);
            const alloc = this.allocateByPct(Number(row.amount), pct);
            row.m1spread = alloc[0];
            row.m2spread = alloc[1];
            row.m3spread = alloc[2];
            row.m4spread = alloc[3];
            row.m5spread = alloc[4];
            row.m6spread = alloc[5];
            row.m7spread = alloc[6];
            row.m8spread = alloc[7];
            row.m9spread = alloc[8];
            row.m10spread = alloc[9];
            row.m11spread = alloc[10];
            row.m12spread = alloc[11];
          }
        } else if (spread === 'Rev') {
          const amt = Number(row.amount) || 0;
          if (!amt) {
            clear = true;
          } else {
            // Use revenue BUDGET spreads as weights so Rev is independent of Actuals
            const revenueAbs = Array.from({length: 12}, (_, i) => Math.abs(this.getRevenueMonthBudget(i + 1)));
            const sumAbs = revenueAbs.reduce((s, v) => s + (Number(v) || 0), 0);

            // If there’s no revenue budget spread (sumAbs === 0), allocate zeros
            const pct = sumAbs > 0
              ? revenueAbs.map(v => (Number(v) || 0) / sumAbs)
              : Array(12).fill(0);

            // Allocate by absolute amount, then apply the original sign back to each bucket
            const sign = amt < 0 ? -1 : 1;
            const allocAbs = this.allocateByPct(Math.abs(amt), pct);
            const alloc = allocAbs.map(v => sign * v);

            row.m1spread = alloc[0];
            row.m2spread = alloc[1];
            row.m3spread = alloc[2];
            row.m4spread = alloc[3];
            row.m5spread = alloc[4];
            row.m6spread = alloc[5];
            row.m7spread = alloc[6];
            row.m8spread = alloc[7];
            row.m9spread = alloc[8];
            row.m10spread = alloc[9];
            row.m11spread = alloc[10];
            row.m12spread = alloc[11];
          }
        } else if (spread === 'LY') {
          // Fill months from last year's actual monthly values for this account
          // Requirement: Do not scale by current amount; use previous-year monthly values directly
          // Fetch from API based on header.start_date - 12 months window
          this.fetchLyForRow(row).then(() => {
            // After filling months from LY, set amount to the sum of those periods per requirement
            row.amount = this.sumMonthsBudget(row);
            this.calcTotal();
          });
        } else if (spread === 'Detail') {
          // Clear spread markers and sync amount from spread months
          for (let i = 1; i <= 12; i++) {
            row[`m${i}spread`] = undefined;
          }
          row.amount = this.sumMonthsBudget(row);
        } else {
          row.m1spread = 0;
          row.m2spread = 0;
          row.m3spread = 0;
          row.m4spread = 0;
          row.m5spread = 0;
          row.m6spread = 0;
          row.m7spread = 0;
          row.m8spread = 0;
          row.m9spread = 0;
          row.m10spread = 0;
          row.m11spread = 0;
          row.m12spread = 0;
        }

        if (clear) {
          row.amount = 0;
          row.m1spread = 0;
          row.m2spread = 0;
          row.m3spread = 0;
          row.m4spread = 0;
          row.m5spread = 0;
          row.m6spread = 0;
          row.m7spread = 0;
          row.m8spread = 0;
          row.m9spread = 0;
          row.m10spread = 0;
          row.m11spread = 0;
          row.m12spread = 0;
        }

        this.calcTotal();
        break;
      }
    }
  }

  calcTotal(): void {
    // If any revenue line changed, and there are lines using Rev spread, recompute those spreads on the fly
    this.applyRevenueSpreadToRevLines();
    this.budget.netIncome = {
      m1: 0, m2: 0, m3: 0, m4: 0, m5: 0, m6: 0,
      m7: 0, m8: 0, m9: 0, m10: 0, m11: 0, m12: 0,
      history: 0
    };


    const signAmount = (type: string, value: number) => {
      const v = Number(value) || 0;
      return type === 'Revenue' ? (0 - v) : v;
    };

    for (let x = 0; x < this.budget.detail.length; x++) {
      const row = this.budget.detail[x];
      // Ensure header_id exists on each detail
      row.header_id = this.budget.header.id;
      // Do not default account_id; it must come from selected account
      // Ensure debit_credit reflects signed total amount per account
      row.debit_credit = signAmount(row.type_name, row.amount || 0);

      if (row.type_name === 'Revenue') {
        if (row.history) {
          this.budget.netIncome.history += Number(row.history);
        }
        if (Number(row.m1)) {
          this.budget.netIncome.m1 += Number(row.m1);
        }
        if (Number(row.m2)) {
          this.budget.netIncome.m2 += Number(row.m2);
        }
        if (Number(row.m3)) {
          this.budget.netIncome.m3 += Number(row.m3);
        }
        if (Number(row.m4)) {
          this.budget.netIncome.m4 += Number(row.m4);
        }
        if (Number(row.m5)) {
          this.budget.netIncome.m5 += Number(row.m5);
        }
        if (Number(row.m6)) {
          this.budget.netIncome.m6 += Number(row.m6);
        }
        if (Number(row.m7)) {
          this.budget.netIncome.m7 += Number(row.m7);
        }
        if (Number(row.m8)) {
          this.budget.netIncome.m8 += Number(row.m8);
        }
        if (Number(row.m9)) {
          this.budget.netIncome.m9 += Number(row.m9);
        }
        if (Number(row.m10)) {
          this.budget.netIncome.m10 += Number(row.m10);
        }
        if (Number(row.m11)) {
          this.budget.netIncome.m11 += Number(row.m11);
        }
        if (Number(row.m12)) {
          this.budget.netIncome.m12 += Number(row.m12);
        }
      } else {
        if (row.history) {
          this.budget.netIncome.history -= Number(row.history);
        }
        if (Number(row.m1)) {
          this.budget.netIncome.m1 -= Number(row.m1);
        }
        if (Number(row.m2)) {
          this.budget.netIncome.m2 -= Number(row.m2);
        }
        if (Number(row.m3)) {
          this.budget.netIncome.m3 -= Number(row.m3);
        }
        if (Number(row.m4)) {
          this.budget.netIncome.m4 -= Number(row.m4);
        }
        if (Number(row.m5)) {
          this.budget.netIncome.m5 -= Number(row.m5);
        }
        if (Number(row.m6)) {
          this.budget.netIncome.m6 -= Number(row.m6);
        }
        if (Number(row.m7)) {
          this.budget.netIncome.m7 -= Number(row.m7);
        }
        if (Number(row.m8)) {
          this.budget.netIncome.m8 -= Number(row.m8);
        }
        if (Number(row.m9)) {
          this.budget.netIncome.m9 -= Number(row.m9);
        }
        if (Number(row.m10)) {
          this.budget.netIncome.m10 -= Number(row.m10);
        }
        if (Number(row.m11)) {
          this.budget.netIncome.m11 -= Number(row.m11);
        }
        if (Number(row.m12)) {
          this.budget.netIncome.m12 -= Number(row.m12);
        }
      }
    }
  }

  /**
   * Fetches table data based on the provided table name, parameters, and additional configuration.
   *
   * @param {string} tableName - Name of the table to fetch data from.
   * @param {string} params - Query parameters for the data fetch operation.
   * @param {function} fun - Callback function to handle the fetched data.
   * @return {Promise<any>} - A promise resolving with the fetched table data.
   */
  async getTableData(tableName: string, params: string, fun: (data: any) => void): Promise<any> {
    let queryChar = '&';
    if (tableName === 'account_subtotal_no_parents') {
      queryChar = '?';
    }
    this.isLoading = true;

    this.dataService
      .getData(tableName, params + queryChar + 'pageNumber=0&pageSize=99999')
      .subscribe((result: ResponseModel) => {
        fun(result.Data);
      });
  }

  /**
   * Set the temporary account detail ID for the account select modal
   */
  setTempAccountDetailId(id: string): void {
    this.vm.tempAccountDetailId = id;
  }

  /**
   * Print the summary table in landscape orientation
   */
  printSummaryTable(): void {
    // Store the current state of showBudget
    const originalShowBudgetState = this.showBudget;

    // Make sure the budget is shown before printing
    if (!this.showBudget) {
      this.showBudget = true;
    }

    // Allow time for the DOM to update
    setTimeout(() => {
      try {
        const container = this.summaryTable?.nativeElement as HTMLElement;
        if (container) {
          const header = this.budget?.header || {} as BudgetHeader;
          const docNumber = header.number ? ` ${header.number}` : '';
          const desc = header.description ? ` - ${header.description}` : '';
          const title = `${this.budgetTypeName} Budget Summary${docNumber}${desc}`.trim();
          const dateRange = `${this.budget.header.start_date || ''} to ${this.budgetEndDate || ''}`;
          const extraMeta = [
            {label: 'Company', value: this.getCompanyName()},
            {label: 'Location', value: this.getLocationName()},
            {label: 'Description', value: this.budget.header.description}
          ];

          const html = this.helperService.buildPdfHtml(container.innerHTML, title, dateRange, extraMeta);
          this.helperService.printHtml(html);
        }
      } catch (e) {
        console.error('Error printing budget summary:', e);
      } finally {
        // Restore the original state of showBudget
        setTimeout(() => {
          this.showBudget = originalShowBudgetState;
        }, 1000);
      }
    }, 250);
  }

  // Recompute month allocations for all lines with spread === 'Rev' based on current Revenue rows
  private applyRevenueSpreadToRevLines(): void {
    if (!this.budget?.detail?.length) {
      return;
    }

    // Build weights from Revenue BUDGET months (m1spread..m12spread), not Actuals
    const revenueAbs = Array.from({length: 12}, (_, i) => Math.abs(this.getRevenueMonthBudget(i + 1)));
    const sumAbs = revenueAbs.reduce((s, v) => s + (Number(v) || 0), 0);
    const pct = sumAbs > 0
      ? revenueAbs.map(v => (Number(v) || 0) / sumAbs)
      : Array(12).fill(0);

    // Apply to each non-revenue detail row that chose Rev spread
    for (const row of this.budget.detail) {
      if (row.spread === 'Rev' && Number(row.amount)) {
        const amt = Number(row.amount) || 0;
        const sign = amt < 0 ? -1 : 1;
        const allocAbs = this.allocateByPct(Math.abs(amt), pct);
        const alloc = allocAbs.map(v => sign * v);
        // Only write BUDGET spread months; do NOT touch Actuals (m1..m12)
        row.m1spread = alloc[0];
        row.m2spread = alloc[1];
        row.m3spread = alloc[2];
        row.m4spread = alloc[3];
        row.m5spread = alloc[4];
        row.m6spread = alloc[5];
        row.m7spread = alloc[6];
        row.m8spread = alloc[7];
        row.m9spread = alloc[8];
        row.m10spread = alloc[9];
        row.m11spread = alloc[10];
        row.m12spread = alloc[11];
      }
    }
  }

  /**
   * Export the summary table to Excel
   */
  exportSummaryTableToExcel(): void {
    // Store the current state of showBudget
    const originalShowBudgetState = this.showBudget;

    // Make sure the budget is shown before exporting
    if (!this.showBudget) {
      this.showBudget = true;
    }

    // Allow time for the DOM to update
    setTimeout(() => {
      // Get the table element from the summary-table div
      const summaryTableElement = this.summaryTable.nativeElement.querySelector('table');

      // Generate a filename based on the budget type and date
      const fileName = `${this.budgetTypeName}_Budget_${new Date().toISOString().slice(0, 10)}.xlsx`;

      // Export the table to Excel
      this.dataExportService.exportToExcel(summaryTableElement, fileName, 'Budget Summary');

      // Restore the original state of showBudget
      setTimeout(() => {
        this.showBudget = originalShowBudgetState;
      }, 1000);
    }, 100);
  }

  /**
   * Get company name from company ID
   */
  getCompanyName(): string {
    if (!this.budget.header.company_id || !this.companies || this.companies.length === 0) {
      return '';
    }
    const company = this.companies.find(c => c.id === this.budget.header.company_id);
    return company ? company.name : '';
  }

  /**
   * Get location name from location ID
   */
  getLocationName(): string {
    if (!this.budget.header.location_id || !this.locations || this.locations.length === 0) {
      return '';
    }
    const location = this.locations.find(l => l.id === this.budget.header.location_id);
    return location ? location.name : '';
  }

  // Put these in the component class
  formatInt(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const n = Number(value);
    if (!isFinite(n)) {
      return '';
    }
    // No decimals; add thousands separators
    return new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(Math.trunc(n));
  }

  parseIntLoose(value: string): number {
    if (!value) {
      return 0;
    }
    // keep leading minus, strip everything else that is not a digit
    const cleaned = value.replace(/(?!^-)[^\d]/g, '');
    // preserve a single leading '-'
    const sign = value.trim().startsWith('-') ? -1 : 1;
    const n = Number(cleaned);
    return isFinite(n) ? sign * Math.trunc(n) : 0;
  }

  /**
   * Handle account selection from the modal
   */
  selectAccountModal(accountId: string): void {
    // check if account already in budget
    const accountInBudget = this.budget.detail.find(row => row.account_id === accountId);
    if (accountInBudget !== undefined) {
      this.notificationService.showWarningToast('Account already in budget');
      accountId = '';
      return;
    }

    // Find the budget detail item with the tempAccountDetailId
    for (const budgetItem of this.budget.detail) {
      if (budgetItem.id === this.vm.tempAccountDetailId) {
        // Find the selected account
        for (const account of this.accounts) {
          if (account.id === accountId) {
            // Check if the account type is allowed (Revenue (4), Costs (5), or Expenses (6))
            const accountType = account.type_name.toLowerCase();
            if (accountType === 'revenue' || accountType === 'expenses' || accountType === 'costs') {
              // Update the budget item with the selected account's details
              // Keep the row id stable; do NOT overwrite it with account.id
              budgetItem.account_id = account.id;
              budgetItem.number = account.number;
              budgetItem.name = account.name;
              budgetItem.type_name = account.type_name;

              // Set the history value from the account object if available
              if (account.history !== undefined) {
                budgetItem.history = account.history;
              }

              // Always fetch actuals on selection when a Start Date is present (fix: reselecting existing account should refresh actuals)
              if (this.budget?.header?.start_date) {
                const sd = new Date(this.budget.header.start_date);
                const signed = false;
                const startDate = sd.toISOString();
                const endDate = this.addMonthsToDate(sd, 12).toISOString();
                const params = `?accountId=${encodeURIComponent(String(account.id))}&startDate=${startDate}&endDate=${endDate}&signed=${signed}`;
                this.dataService.getData('budget/account-sum', params).subscribe({
                  next: (res: any) => {
                    // console.log('account data', res);
                    const data = (res && res.Data) ? res.Data : res;
                    // If API returns an array of accounts with months, apply to rows
                    if (Array.isArray(data)) {
                      this.applyMonthlyActuals(data);
                    } else {
                      // Backward compatibility: single number or object
                      const actual = typeof data === 'number' ? data : (data?.sum ?? data?.history ?? 0);
                      budgetItem.history = Number(actual) || 0;
                    }
                    (budgetItem as any).isNewLine = false;
                    this.calcTotal();
                  },
                  error: () => {
                    (budgetItem as any).isNewLine = false;
                    this.calcTotal();
                  }
                });
              } else {
                this.calcTotal();
              }
            } else {
              // Show toast message for invalid account type
              // console.log('Invalid account type: ' + accountType);
              this.notificationService.showWarningToast('Only Revenue (type 4), Costs (type 5), or Expenses (type 6) account types are allowed.');
            }
            break;
          }
        }
        break;
      }
    }
  }

  /**
   * add x months to a date.
   */
  addMonthsToDate(date: Date, monthsToAdd: number): Date {
    const newDate = new Date(date); // copy
    newDate.setMonth(newDate.getMonth() + monthsToAdd);
    // Set the day to 0 of the *next* month to get the last day of the *current* month
    // For example, if the newDate is in January, setting the day to 0 of February
    // will result in January 31st (or 30th, etc. depending on the month)
    newDate.setDate(0);
    return newDate;
  }

  getOneYearAgoToday(): Date {
    const today = new Date();
    const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    return oneYearAgo;
  }

  /**
   * Apply monthly actuals from API payload to matching rows by account_id.
   * Payload may include 1..N accounts, each with months entries for a 12-month window.
   */
  private applyMonthlyActuals(payload: any[]): void {
    if (!payload || !this.budget?.detail?.length) {
      return;
    }

    // Normalize payload into an array of { account_id|accountId|id, months?, debit_credit? }
    const normalize = (raw: any): any[] => {
      if (Array.isArray(raw)) {
        return raw;
      }
      if (raw && Array.isArray(raw.accounts)) {
        return raw.accounts;
      }
      // Object map keyed by account_id
      if (raw && typeof raw === 'object') {
        return Object.values(raw);
      }
      return [];
    };

    const rows = normalize(payload);

    // Index current budget rows by account_id for quick updates
    const byId: Record<string, any> = {};
    for (const row of this.budget.detail) {
      if (row.account_id != null) {
        byId[String(row.account_id)] = row;
      }
    }

    // Helper to read account id from payload row (accept several common shapes)
    const getAcctId = (a: any) => (
      a?.account_id ?? a?.accountId ?? a?.id ?? a?.AccountId ?? a?.Account_id
    );

    // Base start date for month index mapping
    const base = this.budget?.header?.start_date ? new Date(this.budget.header.start_date as any) : null;
    const monthIndex = (y: number, m1to12: number): number => {
      if (!base) {
        return m1to12;
      } // fallback
      const d = new Date(y, m1to12 - 1, 1);
      const monthsDiff = (d.getFullYear() - base.getFullYear()) * 12 + (d.getMonth() - base.getMonth());
      return monthsDiff + 1; // 1..12
    };

    const toNumber = (v: any): number => Number(v ?? 0) || 0;

    for (const acct of rows) {
      const key = getAcctId(acct);
      const target = key != null ? byId[String(key)] : null;
      if (!target) {
        continue;
      }

      // Set total actual if provided
      if (acct.debit_credit !== undefined || acct.sum !== undefined || acct.amount !== undefined || acct.value !== undefined) {
        const total = toNumber(acct.debit_credit ?? acct.sum ?? acct.amount ?? acct.value);
        target.history = total;
      }

      // Clear previous month actuals (store in m1..m12)
      for (let i = 1; i <= 12; i++) {
        (target as any)[`m${i}`] = 0;
      }

      // Apply monthly entries if present
      const months = Array.isArray(acct.months) ? acct.months
        : Array.isArray(acct.Months) ? acct.Months
          : Array.isArray(acct.month_list) ? acct.month_list
            : Array.isArray(acct.MonthList) ? acct.MonthList
              : [];

      for (const m of months) {
        // Accept different month field names and normalize to 1..12
        const monRaw = m.month ?? m.month_no ?? m.monthNumber ?? m.mon;
        let mon = Number(monRaw);
        if (Number.isFinite(mon)) {
          // If API is 0-based (0..11), convert to 1..12
          if (mon >= 0 && mon <= 11) {
            mon = mon + 1;
          }
        } else {
          continue; // skip if invalid
        }

        const yr = Number(m.year ?? m.yy ?? m.yyyy ?? m.fy ?? base?.getFullYear());
        const idx = monthIndex(yr, mon);

        if (idx >= 1 && idx <= 12) {
          const val = toNumber(m.debit_credit ?? m.sum ?? m.amount ?? m.value ?? m.dc);
          (target as any)[`m${idx}`] = val;
        }
      }
    }
  }

  /**
   * Process account data to ensure it has all required properties
   */
  processAccountsData(accounts: any[]): any[] {
    return accounts.map(account => {
      // Ensure the account has all required properties
      return {
        ...account,
        // Add history property if not present (default to 0)
        history: account.history || 0,
        // Ensure type_name is present
        type_name: account.type_name || this.getAccountTypeName(account),
        // Ensure subtotal_name is present
        subtotal_name: account.subtotal_name || ''
      };
    });
  }

  /**
   * Determine account type name based on account properties
   */
  getAccountTypeName(account: any): string {
    // Logic to determine account type based on account properties
    // This is a simplified example - adjust based on your actual account structure
    if (account.number && account.number.startsWith('4')) {
      return 'Revenue';
    } else if (account.number && account.number.startsWith('5')) {
      return 'Costs';
    } else if (account.number && account.number.startsWith('6')) {
      return 'Expenses';
    } else if (account.number && account.number.startsWith('1')) {
      return 'Assets';
    } else if (account.number && account.number.startsWith('2')) {
      return 'Liabilities';
    } else {
      return 'Other';
    }
  }

  /**
   * Refresh the accounts list
   */
  updateAccount(): void {
    this.vm.startAccount = 0;
    this.getTableData('account', '?state=active', (data) => {
      // Process accounts to ensure they have all required properties
      this.accounts = this.processAccountsData(data);
      this.vm.account = this.accounts; // Also update vm.account for the modal component
      // console.log('accounts refreshed with history', this.accounts);
    }).then();
  }

  /**
   * Add a new row to the budget detail table
   */
  addBudgetDetailRow(): void {
    // Generate a temp unique ID for the new row - gets overwritten by api on save
    const newId = Date.now().toString();

    // Create a new budget detail item with default values
    const newBudgetDetail = {
      isNewLine: true,
      id: newId,
      header_id: this.budget.header.id,
      account_id: '',
      number: '',
      name: 'Select an account',
      type_name: '',
      amount: 0,
      history: 0,
      spread: '',
      sequence: (this.budget.detail?.length || 0) + 1,
      m1: 0,
      m2: 0,
      m3: 0,
      m4: 0,
      m5: 0,
      m6: 0,
      m7: 0,
      m8: 0,
      m9: 0,
      m10: 0,
      m11: 0,
      m12: 0
    } as any;

    // Add the new item to the budget detail array
    this.budget.detail.push(newBudgetDetail);

    // Recalculate totals
    this.calcTotal();
  }

  /**
   * Remove a row from the budget detail table by ID
   */
  removeBudgetDetailRow(id: string): void {
    // Find the index of the row with the given ID
    const index = this.budget.detail.findIndex(item => item.id === id);

    // If the row is found, remove it
    if (index !== -1) {
      this.budget.detail.splice(index, 1);

      // Enforce spread rules after deletion: Revenue rows cannot use Rev
      for (const row of this.budget.detail) {
        if (row.type_name === 'Revenue' && row.spread === 'Rev') {
          row.spread = '';
        }
      }

      // Recalculate totals to update the summary table
      this.calcTotal();
    }
  }
}
